import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CLINIC, DOCTORS, PHOTOS, FAQS, SEO, NAV } from "@/data/site";

const FAQ_KEY = {
  "/": "home",
  "/about": "about",
  "/services": "services",
  "/preventive-heart-care": "preventive",
  "/womens-heart-health": "womens",
  "/general-care": "general_care",
  "/team": "team",
  "/contact": "contact",
};

function postalAddress() {
  return {
    "@type": "PostalAddress",
    streetAddress: "B 37/45 B-4 Kabir Nagar Road, Durgakund Rd, near Sanjay Siksha Niketan, Kabir Nagar",
    addressLocality: "Varanasi",
    addressRegion: "Uttar Pradesh",
    postalCode: "221005",
    addressCountry: "IN",
  };
}

function openingHours() {
  return [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "10:00",
      closes: "19:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "10:00",
      closes: "16:00",
    },
  ];
}

function buildGraph(pathname, origin) {
  const meta = SEO[pathname] || SEO["/"];
  const address = postalAddress();

  const physician = {
    "@type": "Physician",
    "@id": `${origin}/#physician`,
    name: DOCTORS.pallavi.name,
    image: PHOTOS.pallavi,
    url: origin,
    telephone: CLINIC.phone,
    email: CLINIC.email,
    medicalSpecialty: "Cardiovascular",
    areaServed: "Varanasi, Uttar Pradesh",
    address,
    knowsAbout: DOCTORS.pallavi.expertise,
  };

  const clinic = {
    "@type": ["MedicalClinic", "LocalBusiness"],
    "@id": `${origin}/#clinic`,
    name: "Dr. Pallavi Mishra — Cardiology Clinic, Varanasi",
    description: meta.description,
    image: PHOTOS.pallavi,
    url: origin,
    telephone: CLINIC.phone,
    email: CLINIC.email,
    priceRange: "₹₹",
    address,
    geo: {
      "@type": "GeoCoordinates",
      latitude: CLINIC.geo.lat,
      longitude: CLINIC.geo.lng,
    },
    hasMap: CLINIC.directionsUrl,
    openingHoursSpecification: openingHours(),
    medicalSpecialty: "Cardiovascular",
    availableService: [
      { "@type": "MedicalProcedure", name: "Preventive Cardiology Assessment" },
      { "@type": "MedicalProcedure", name: "Non-Invasive Cardiac Diagnostics" },
      { "@type": "MedicalProcedure", name: "Echocardiography Review" },
      { "@type": "MedicalProcedure", name: "Women's Heart Health" },
      { "@type": "MedicalProcedure", name: "General Physician Care" },
    ],
    employee: [{ "@id": `${origin}/#physician` }],
  };

  // Breadcrumbs
  const crumbs = [{ name: "Home", url: `${origin}/` }];
  if (pathname !== "/") {
    const nav = NAV.find((n) => n.to === pathname);
    crumbs.push({ name: nav ? nav.label : pathname.replace("/", ""), url: `${origin}${pathname}` });
  }
  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };

  const graph = [physician, clinic, breadcrumb];

  // FAQPage for the current route
  const faqs = FAQS[FAQ_KEY[pathname]];
  if (faqs && faqs.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

export function useSchema() {
  const { pathname } = useLocation();
  useEffect(() => {
    const origin = window.location.origin;
    const id = "ld-json-schema";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildGraph(pathname, origin));
  }, [pathname]);
}
